/**
 * The platform admin: for whoever runs the platform, over every restaurant.
 *
 * Deliberately unlike a restaurant's back office in one way: the side rail
 * says, in words and in colour, that this is the platform admin and who is
 * signed in. An operator can suspend any restaurant from here, and nobody
 * should mistake this tab for the back office open beside it.
 *
 * A rail rather than tabs, because an operator works at a desk and the
 * sections keep growing; on a narrow screen it folds into a drawer. Overview
 * (health and usage) is the page an operator lands on.
 */

import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router-dom';
import {
  displayLocale,
  useFreshOperatorRecoveryCodes,
  useOperator,
  useOperatorSignOut,
  usePlatformEnquiries,
} from '@restaurant/api-client';
import { ActionError, Boundary, Button, Empty, Field, Input, Sheet } from '@restaurant/ui';

import { RecoveryCodes } from './authenticator';
import { ActivityPage } from './pages/activity';
import { BillingPage } from './pages/billing';
import { OverviewPage } from './pages/overview';
import { RestaurantPage } from './pages/restaurant';
import { RestaurantsPage } from './pages/restaurants';
import { EnquiriesPage } from './pages/enquiries';
import { SupportPage } from './pages/support';
import { WebsitePage } from './pages/website';
import { formatMoment } from './words';

/**
 * The sections, grouped by what an operator is doing: watching the platform,
 * looking after the restaurants on it, and answering the ones that might be.
 */
const GROUPS: readonly {
  title: string;
  links: readonly { to: string; label: string; end?: boolean; count?: 'enquiries' }[];
}[] = [
  {
    title: 'Platform',
    links: [
      { to: '/', label: 'Overview', end: true },
      { to: '/website', label: 'Website' },
      { to: '/activity', label: 'Operator activity' },
    ],
  },
  {
    title: 'Restaurants',
    links: [
      { to: '/restaurants', label: 'Restaurants' },
      { to: '/billing', label: 'Billing' },
      { to: '/support', label: 'Support access' },
    ],
  },
  {
    title: 'New business',
    links: [{ to: '/enquiries', label: 'Enquiries', count: 'enquiries' }],
  },
];

export function App() {
  const [account, setAccount] = useState(false);
  const [menu, setMenu] = useState(false);
  const location = useLocation();

  // A section chosen from the drawer closes it.
  useEffect(() => setMenu(false), [location.pathname]);

  return (
    <div className={`pf${menu ? ' is-menu-open' : ''}`}>
      <header className="pf__top">
        <button
          type="button"
          className="pf__menu-btn"
          aria-expanded={menu}
          aria-controls="pf-side"
          onClick={() => setMenu((open) => !open)}
        >
          <span aria-hidden="true">☰</span> Menu
        </button>
        <Brand />
      </header>

      <aside id="pf-side" className="pf__side" aria-label="Sections">
        <Brand />
        <nav className="pf__nav">
          {GROUPS.map((group) => (
            <div key={group.title} className="pf__group">
              <p className="pf__group-title">{group.title}</p>
              {group.links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end ?? false}
                  className={({ isActive }) => `pf__link${isActive ? ' is-active' : ''}`}
                >
                  <span>{link.label}</span>
                  {link.count === 'enquiries' && <EnquiryCount />}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <Operator onOpen={() => setAccount(true)} />
      </aside>
      {menu && (
        <button
          type="button"
          className="pf__scrim"
          aria-label="Close the menu"
          onClick={() => setMenu(false)}
        />
      )}

      <main className="pf__main">
        <div className="pf__page">
          <Boundary label="This page">
            <Routes>
              <Route index element={<OverviewPage />} />
              <Route path="/website" element={<WebsitePage />} />
              <Route path="/restaurants" element={<RestaurantsPage />} />
              <Route path="/restaurants/:restaurantId" element={<RestaurantPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/enquiries" element={<EnquiriesPage />} />
              <Route path="/activity" element={<ActivityPage />} />
              <Route path="*" element={<Empty title="That page does not exist" />} />
            </Routes>
          </Boundary>
        </div>
      </main>

      {account && <AccountSheet onDismiss={() => setAccount(false)} />}
    </div>
  );
}

/** Says, in words and in the accent, that this is the platform and not a
 *  restaurant's back office open in the next tab. */
function Brand() {
  return (
    <span className="pf__brand">
      <span className="pf__brand-mark" aria-hidden="true">
        On
      </span>
      <span className="pf__brand-words">
        OnRestaurant
        <span className="pf__brand-tag">Platform admin</span>
      </span>
    </span>
  );
}

function EnquiryCount() {
  const enquiries = usePlatformEnquiries();
  const waiting = (enquiries.data ?? []).filter((e) => e.handled_at === null).length;
  if (waiting === 0) return null;
  return (
    <span className="pf__count" aria-label={`${waiting} waiting`}>
      {waiting}
    </span>
  );
}

function Operator({ onOpen }: { onOpen: () => void }) {
  const operator = useOperator();
  const signOut = useOperatorSignOut();
  const name = operator.data?.name ?? '';
  return (
    <div className="pf__operator">
      <button type="button" className="pf__who" onClick={onOpen}>
        <span className="pf__avatar" aria-hidden="true">
          {name.trim().charAt(0).toUpperCase() || '?'}
        </span>
        <span className="pf__who-words">
          <span className="pf__who-name">{name}</span>
          <span className="pf__who-email">{operator.data?.email}</span>
        </span>
      </button>
      <Button
        variant="ghost"
        size="sm"
        block
        loading={signOut.isPending}
        onClick={() => signOut.mutate()}
      >
        Sign out
      </Button>
    </div>
  );
}

function AccountSheet({ onDismiss }: { onDismiss: () => void }) {
  const operator = useOperator();
  const fresh = useFreshOperatorRecoveryCodes();
  const [code, setCode] = useState('');
  const left = operator.data?.recovery_codes_left ?? 0;

  if (fresh.data) {
    return (
      <Sheet
        title="Your new recovery codes"
        onDismiss={onDismiss}
        actions={
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            I have saved them
          </Button>
        }
      >
        <p className="pf__sub">
          Your old recovery codes stopped working the moment these appeared.
        </p>
        <RecoveryCodes codes={fresh.data.recovery_codes} />
      </Sheet>
    );
  }

  return (
    <Sheet title="Your login" onDismiss={onDismiss}>
      <dl className="pf__facts">
        <dt>Signed in as</dt>
        <dd>
          {operator.data?.name} ({operator.data?.email})
        </dd>
        <dt>Since</dt>
        <dd>
          {operator.data ? formatMoment(operator.data.signed_in_at, displayLocale()) : '—'}
        </dd>
        <dt>Recovery codes left</dt>
        <dd>{left}</dd>
      </dl>
      {left <= 2 && (
        <p className="pf__warn">
          {left === 0
            ? 'No recovery codes left: a lost phone would lock you out. Take a new set.'
            : 'Few recovery codes left. Take a new set before they run out.'}
        </p>
      )}
      <p className="pf__sub">
        A new set replaces the codes you have. Type the code your authenticator app shows now.
      </p>
      <Field
        label="Code from your app"
        error={
          fresh.error?.status === 401
            ? 'That code is not right. Wait for the next one.'
            : undefined
        }
      >
        <Input
          className="pf__code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
        />
      </Field>
      <Button
        disabled={code.trim() === ''}
        loading={fresh.isPending}
        onClick={() => fresh.mutate({ code: code.replace(/\s+/g, '') })}
      >
        New recovery codes
      </Button>
      {fresh.error && fresh.error.status !== 401 && (
        <ActionError error={fresh.error} action="New recovery codes" />
      )}
      <p className="pf__sub">
        Switching an operator off, or adding one, is done on the server with{' '}
        <code>scripts/platform_operator.py</code>.
      </p>
    </Sheet>
  );
}
